// @givmo/crm — the one quiet contacts table every tab of the contacts page draws (CRM-5).
//
// A row opens its record: a Givmo donor's record page, or one of the organization's own contacts'
// panel.
//
// Name, email, tags, last gift and, where a list holds both kinds of record, the source in plain
// words. A contact the organization may not email says so after its address, as a word
// ("Unsubscribed"), never a badge. Rows are never merged: the table draws exactly the rows it is
// handed, one per record, whatever two of them share.

import type { MouseEvent, ReactNode } from 'react';
import {
  Badge,
  Button,
  Card,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  testIdProp,
} from '@givmo/design-system';
import { displayDate, displayGroupedCount, displayText } from './crm-decode';

/** The plain word a contact the organization may not email carries after its address. */
export const DO_NOT_EMAIL_LABEL = 'Unsubscribed';

export interface ContactsTableRow {
  key: string;
  testId: string;
  /** The name cell, already rendered (a link to the record, or the text). */
  name: ReactNode;
  email: string | null | undefined;
  doNotEmail: boolean;
  tags: readonly string[];
  lastGift: string | null | undefined;
  /** The source in plain words; drawn only when the table shows its Source column. */
  source?: string;
  /** Trailing row controls, drawn only when the table has an actions column. */
  actions?: ReactNode;
  /**
   * Opens the row's record (a donor record, or a contact's panel) on a click anywhere in the row. The
   * name cell carries the same as a link or a button, which is how a keyboard reaches it.
   */
  onOpen?: () => void;
}

/** A click that landed on the row's own link or button already did its job. */
function openFromRow(event: MouseEvent<HTMLTableRowElement>, onOpen: () => void): void {
  const target = event.target as HTMLElement | null;
  if (target?.closest('a, button, input, label') != null) return;
  // Selecting text in a cell is not a request to open it.
  if ((window.getSelection?.()?.toString() ?? '').length > 0) return;
  onOpen();
}

export function ContactsTable({
  rows,
  lastGiftLabel,
  showSource,
  showActions = false,
  listTestId,
  footer,
}: {
  rows: readonly ContactsTableRow[];
  lastGiftLabel: string;
  showSource: boolean;
  showActions?: boolean;
  listTestId: string;
  footer?: ReactNode;
}) {
  return (
    <Card className="overflow-hidden" {...testIdProp(listTestId)}>
      <div className="overflow-x-auto">
        <Table className="min-w-[44rem]">
          <THead>
            <tr>
              <TH>Name</TH>
              <TH>Email</TH>
              <TH>Tags</TH>
              <TH>{lastGiftLabel}</TH>
              {showSource ? <TH>Source</TH> : null}
              {showActions ? (
                <TH align="right">
                  <span className="sr-only">Actions</span>
                </TH>
              ) : null}
            </tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR
                key={row.key}
                testId={row.testId}
                interactive={row.onOpen !== undefined}
                onClick={
                  row.onOpen === undefined
                    ? undefined
                    : (event) => openFromRow(event, row.onOpen as () => void)
                }
              >
                <TD>{row.name}</TD>
                <TD>
                  <span className="text-text-secondary">{displayText(row.email)}</span>
                  {row.doNotEmail ? (
                    <span className="text-text-strong" {...testIdProp('crm-do-not-email')}>
                      {' · '}
                      {DO_NOT_EMAIL_LABEL}
                    </span>
                  ) : null}
                </TD>
                <TD>
                  {row.tags.length === 0 ? (
                    <span className="text-text-tertiary">—</span>
                  ) : (
                    <span className="flex flex-wrap gap-6">
                      {row.tags.map((tag) => (
                        <Badge key={tag} tone="neutral" size="sm">
                          {tag}
                        </Badge>
                      ))}
                    </span>
                  )}
                </TD>
                <TD>
                  <span className="text-text-tertiary tabular-nums">
                    {displayDate(row.lastGift)}
                  </span>
                </TD>
                {showSource ? (
                  <TD>
                    <span className="text-text-secondary">{row.source}</span>
                  </TD>
                ) : null}
                {showActions ? <TD align="right">{row.actions}</TD> : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
      {footer}
    </Card>
  );
}

/**
 * The table's footer: "1–25 of 2,471 contacts" and Previous / Next. The design system's own footer
 * prints the total ungrouped ("2471"), so this one groups it, as the approved mockup does.
 */
export function ContactsPagination({
  offset,
  shown,
  total,
  busy,
  onPrevious,
  onNext,
  testId,
}: {
  offset: number;
  shown: number;
  total: number;
  busy: boolean;
  onPrevious: () => void;
  onNext: () => void;
  testId: string;
}) {
  const start = shown === 0 ? 0 : offset + 1;
  const end = offset + shown;
  return (
    <div
      className="flex items-center justify-between gap-12 border-t border-border-subtle px-16 py-10"
      {...testIdProp(testId)}
    >
      <span className="font-sans text-s13 tracking-normal text-text-tertiary tabular-nums">
        {displayGroupedCount(start)}–{displayGroupedCount(end)} of {displayGroupedCount(total)}{' '}
        {total === 1 ? 'contact' : 'contacts'}
      </span>
      <div className="flex items-center gap-6">
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          disabled={offset === 0 || busy}
          onClick={onPrevious}
          testId={`${testId}-prev`}
        >
          Previous
        </Button>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          disabled={end >= total || busy}
          onClick={onNext}
          testId={`${testId}-next`}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
